
import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
} from "react-router-dom";
import GoogleLoginPopup from "./pages/Consumer/GoogleLoginPopup.tsx";
import AppleLoginPopup from "./pages/Consumer/AppleLoginPopup.tsx";
import FacebookLoginPopup from "./pages/Consumer/FacebookLoginPopup.tsx";
import OAuthCallback from "./pages/Consumer/GoogleCallback.tsx";
import MessageList from "./components/MessageList";
import "./App.css";
import "./styles/variables.css";
import "./index.css";
// Components
import Login from "./pages/Login/Login";
import ForgotPassword from "./pages/ForgotPassword";
import Unauthorized from "./pages/Unauthorized";
import PrivateRoute from "./components/PrivateRoute";
import RoleRoute from "./components/RoleRoute";

// Dashboard components
import SuperAdminDashboard from "./pages/Dashboard/SuperAdminDashboard";
import ClientAdminDashboard from "./pages/Dashboard/ClientAdminDashboard";
import OperatorDashboard from "./pages/Dashboard/OperatorDashboard";
import ConsumerDashboard from "./pages/Dashboard/ConsumerDashboard";

// SuperAdmin pages
import UserList from "./pages/Dashboard/SuperAdminDashboard/UserList";
import OperatorList from "./pages/Dashboard/SuperAdminDashboard/OperatorList";
import EditOperatorTabs from "./pages/Dashboard/SuperAdminDashboard/EditOperatorTabs";
import GroupsPage from "./pages/Dashboard/SuperAdminDashboard/Groups/GroupsPage";
import GroupDetailsPage from "./pages/Dashboard/SuperAdminDashboard/Groups/GroupDetailsPage";
import ManageClients from "./pages/Dashboard/SuperAdminDashboard/ManageClients";
import Reports from "./pages/Dashboard/SuperAdminDashboard/Reports";
import Settings from "./pages/Dashboard/SuperAdminDashboard/Settings";
import DashboardHome from "./pages/Dashboard/SuperAdminDashboard/DashboardHome";
import EditClientTabs from "./pages/Dashboard/SuperAdminDashboard/EditClientTabs";
import EditUserTabs from "./pages/Dashboard/SuperAdminDashboard/EditUserTabs";

// ClientAdmin pages
import ClientReports from "./pages/Dashboard/ClientAdminDashboard/Reports";

// Operator pages
import ChatQueue from "./pages/Dashboard/OperatorDashboard/ChatQueue";
import OperatorProfile from "./pages/Dashboard/OperatorDashboard/Profile";

// Consumer pages
import Messages from "./pages/Dashboard/ConsumerDashboard/Messages";
import ConsumerProfile from "./pages/Dashboard/ConsumerDashboard/Profile";

// Payment pages (Consumer)


// Consumer Widget (public)
import Widget from "./pages/Consumer/Widget.tsx";

// Auth and setup pages
import ContactDetails from "./pages/ContactDetails";
import Password from "./pages/Password";
import TwoFactorMethod from "./pages/TwoFactorMethod";
import TwoFactorApp from "./pages/TwoFactorApp";
import TwoFactorVerify from "./pages/TwoFactorVerify";
import BypassCodes from "./pages/BypassCodes";
import LoginTwoFactor from "./pages/LoginTwoFactor";
import ResetPassword from "./pages/ResetPassword";
import ConsumerResetPassword from "./pages/ConsumerResetPassword.js";
import { SignalRProvider } from "./contextProviders/SignalRProvider";
import { ProxySessionProvider } from "./contextProviders/ProxySessionProvider";

// consumer search component
import ConsumerMainComponent from "./pages/Dashboard/SuperAdminDashboard/Consumer/consumer-main-component.jsx";
import LoadingScreen from "./components/LoadingScreen";

// Component for redirecting legacy group routes
const GroupRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/superadmin/groups/${id}`} replace />;
};

const RedirectBasedOnRole = () => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [authState, setAuthState] = React.useState(null);

  React.useEffect(() => {
    const checkAuthentication = async () => {
      console.log("🔍 Starting authentication check...");
      try {
        // Add minimum loading time to ensure loader is visible
        const startTime = Date.now();
        
        const token = localStorage.getItem("token");
        console.log("🔑 Token found:", !!token);
        
        if (!token) {
          console.log("❌ No token found, showing login");
          // Ensure minimum loading time for smooth UX
          const elapsed = Date.now() - startTime;
          const minDelay = Math.max(800 - elapsed, 0);
          
          await new Promise(resolve => setTimeout(resolve, minDelay));
          setAuthState({ isAuthenticated: false });
          setIsLoading(false);
          return;
        }

        // Validate token format and decode payload
        const tokenParts = token.split(".");
        if (tokenParts.length !== 3) {
          console.log("❌ Invalid token format");
          // Invalid token format
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          localStorage.removeItem("role");
          
          const elapsed = Date.now() - startTime;
          const minDelay = Math.max(800 - elapsed, 0);
          await new Promise(resolve => setTimeout(resolve, minDelay));
          
          setAuthState({ isAuthenticated: false });
          setIsLoading(false);
          return;
        }

        try {
          const payload = JSON.parse(atob(tokenParts[1]));
          console.log("🔓 Token payload:", { role: payload.role, exp: payload.exp });
          
          // Check if token is expired
          const currentTime = Date.now() / 1000;
          if (payload.exp && payload.exp < currentTime) {
            console.log("⏰ Token expired");
            // Token expired
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            localStorage.removeItem("role");
            
            const elapsed = Date.now() - startTime;
            const minDelay = Math.max(800 - elapsed, 0);
            await new Promise(resolve => setTimeout(resolve, minDelay));
            
            setAuthState({ isAuthenticated: false });
            setIsLoading(false);
            return;
          }

          console.log("✅ Token valid, role:", payload.role);
          // Token is valid - ensure minimum loading time for smooth UX
          const elapsed = Date.now() - startTime;
          const minDelay = Math.max(1200 - elapsed, 0); // Longer delay for successful auth
          
          console.log("⏳ Adding delay:", minDelay + "ms");
          await new Promise(resolve => setTimeout(resolve, minDelay));
          
          // Token is valid, set authentication state
          setAuthState({
            isAuthenticated: true,
            role: payload.role
          });
        } catch (decodeError) {
          console.log("❌ Token decode error:", decodeError);
          // Invalid token payload
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          localStorage.removeItem("role");
          
          const elapsed = Date.now() - startTime;
          const minDelay = Math.max(800 - elapsed, 0);
          await new Promise(resolve => setTimeout(resolve, minDelay));
          
          setAuthState({ isAuthenticated: false });
        }
      } catch (error) {
        console.error("💥 Authentication check failed:", error);
        
        // Ensure minimum loading time even on error
        await new Promise(resolve => setTimeout(resolve, 800));
        setAuthState({ isAuthenticated: false });
      } finally {
        console.log("🏁 Authentication check complete, setting loading to false");
        setIsLoading(false);
      }
    };

    checkAuthentication();
  }, []);

  console.log("🎨 Render state:", { isLoading, authState });

  // Show loading screen while checking authentication
  if (isLoading) {
    console.log("📺 Showing loading screen");
    return <LoadingScreen message="Loading your session..." />;
  }

  // If not authenticated, show login
  if (!authState?.isAuthenticated) {
    console.log("🔐 Showing login form");
    return <Login />;
  }

  // Redirect based on role
  console.log("↗️ Redirecting to dashboard for role:", authState.role);
  switch (authState.role) {
    case "SuperAdmin":
      return <Navigate to="/superadmin" replace />;
    case "ClientAdmin":
      return <Navigate to="/clientadmin" replace />;
    case "Operator":
      return <Navigate to="/operator" replace />;
    case "Consumer":
      return <Navigate to="/consumer" replace />;
    default:
      return <Unauthorized />;
  }
};

function App() {
  const accessToken = localStorage.getItem("token");
  return (
    <ProxySessionProvider>
      <SignalRProvider accessToken={accessToken}>
        <Router>
        <Routes>
          {/* Google Login Popup Route for widget/iframe SSO */}
          <Route path="/google-login-popup" element={<GoogleLoginPopup />} />
          {/* Apple Login Popup Route for widget/iframe SSO */}
          <Route path="/apple-login-popup" element={<AppleLoginPopup />} />
          {/* Facebook Login Popup Route for widget/iframe SSO */}
          <Route path="/facebook-login-popup" element={<FacebookLoginPopup />} />
          {/* OAuth Callback Route */}
          <Route path="/auth/callback" element={<OAuthCallback />} />
          {/* Public Routes */}
          {/* <Route path="/" element={<Login />} /> */}
          <Route path="/" element={<RedirectBasedOnRole />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/consumer-reset-password"
            element={<ConsumerResetPassword />}
          />

          {/* Onboarding Routes for new users after email verification */}
          <Route path="/contact-details" element={<ContactDetails />} />
          <Route path="/password" element={<Password />} />
          <Route path="/two-factor-method" element={<TwoFactorMethod />} />
          <Route path="/two-factor-app" element={<TwoFactorApp />} />
          <Route path="/two-factor-verify" element={<TwoFactorVerify />} />
          <Route path="/bypass-codes" element={<BypassCodes />} />

          {/* Login 2FA Route */}
          <Route path="/login-2fa" element={<LoginTwoFactor />} />

          {/* SuperAdmin Routes (nested) - Full access to everything */}
          <Route
            path="/superadmin"
            element={
              <PrivateRoute>
                <RoleRoute allowedRoles={["SuperAdmin"]}>
                  <SuperAdminDashboard />
                </RoleRoute>
              </PrivateRoute>
            }
          >
            <Route index element={<DashboardHome />} />
            <Route path="clients" element={<ManageClients />} />
            <Route path="users" element={<UserList />} />
            <Route path="users/:id/edit" element={<EditUserTabs />} />
            <Route path="operators" element={<OperatorList />} />
            <Route path="operators/:id/edit" element={<EditOperatorTabs />} />
            <Route path="groups" element={<GroupsPage />} />
            <Route path="groups/:id" element={<GroupDetailsPage />} />
            <Route path="reports" element={<Reports />} />
            <Route path="consumers" element={<ConsumerMainComponent />} />
            <Route path="settings" element={<Settings />} />
            <Route path="clients/:id/edit" element={<EditClientTabs />} />
            <Route path="users/:id/edit" element={<EditUserTabs />} />
            <Route path="operators" element={<OperatorList />} />
            <Route path="operators/:id/edit" element={<EditOperatorTabs />} />
            <Route path="messages" element={<MessageList />} />
          </Route>

          {/* ClientAdmin Routes (nested) - NO access to Users and Clients */}
          <Route
            path="/clientadmin"
            element={
              <PrivateRoute>
                <RoleRoute allowedRoles={["ClientAdmin"]}>
                  <ClientAdminDashboard />
                </RoleRoute>
              </PrivateRoute>
            }
          >
            <Route index element={<DashboardHome />} />
            <Route path="operators" element={<OperatorList />} />
            <Route path="operators/:id/edit" element={<EditOperatorTabs />} />
            {/* <Route path="groups" element={<GroupsPage />} />
          <Route path="groups/:id" element={<GroupDetailsPage />} /> */}
            <Route path="messages" element={<MessageList />} />
            {/* <Route path="reports" element={<ClientReports />} /> */}
            <Route path="consumers" element={<ConsumerMainComponent />} />
            <Route path="settings" element={<Settings />} />
          </Route>

          {/* Users Route (SuperAdmin only) */}
          <Route
            path="/superadmin/users"
            element={
              <PrivateRoute>
                <RoleRoute allowedRoles={["SuperAdmin"]}>
                  <SuperAdminDashboard />
                </RoleRoute>
              </PrivateRoute>
            }
          >
            <Route index element={<UserList />} />
          </Route>

          {/* Operator Routes */}
          <Route
            path="/operator"
            element={
              <PrivateRoute>
                <RoleRoute allowedRoles={["Operator"]}>
                  <OperatorDashboard />
                </RoleRoute>
              </PrivateRoute>
            }
          >
            <Route path="" element={<MessageList />} />
            <Route path="profile" element={<OperatorProfile />} />
          </Route>

          {/* Consumer Routes */}
          <Route
            path="/consumer"
            element={
              <PrivateRoute>
                <RoleRoute allowedRoles={["Consumer"]}>
                  <ConsumerDashboard />
                </RoleRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/consumer/messages"
            element={
              <PrivateRoute>
                <RoleRoute allowedRoles={["Consumer"]}>
                  <Messages />
                </RoleRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/consumer/profile"
            element={
              <PrivateRoute>
                <RoleRoute allowedRoles={["Consumer"]}>
                  <ConsumerProfile />
                </RoleRoute>
              </PrivateRoute>
            }
          />

          {/* Consumer Widget (public - no auth required) */}
          <Route path="/widget" element={<Widget />} />

          {/* Legacy route redirects */}
          <Route
            path="/groups"
            element={<Navigate to="/superadmin/groups" replace />}
          />
          <Route path="/groups/:id" element={<GroupRedirect />} />
        </Routes>
      </Router>
    </SignalRProvider>
    </ProxySessionProvider>
  );
}

export default App;
